import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import './index.scss';
import { getIsPadWidth } from '@elem/utils/devices';
import { useGlobal } from '@/utils/useGlobal';

export default function BlogCategoryItems({ categoryData = {}, categoryTagList = [] }: { categoryData: any, categoryTagList?: any }) {
    // 过滤掉 categoryData 中的 tag
    categoryTagList = categoryTagList.filter((item: any) => categoryData.categoryName !== item.tag);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isPad, setIsPad] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const { locale = 'us' } = useRouter();
    const { useLang } = useGlobal();
    const { t } = useLang('blog');
    useEffect(() => {
        const checkPadWidth = () => {
            // 检测是否是小于等于1024端 (h5 + pad)
            setIsPad(getIsPadWidth());
        };
        checkPadWidth();
        window.addEventListener('resize', checkPadWidth);
        return () => window.removeEventListener('resize', checkPadWidth);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                if (!isPad) {
                    setShowDropdown(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isPad]);

    // 计算并设置CSS变量 --sticky-top-offset（仅初始化时）
    useEffect(() => {
        let totalHeight = 0;

        // 获取所有sticky元素的高度
        const header = document.querySelector('.ecoflow-frame-header');
        if (header) {
            totalHeight += header.getBoundingClientRect().height;
        }

        // 如果当前容器也是sticky的，加上它的高度
        if (containerRef.current) {
            const style = window.getComputedStyle(containerRef.current);
            if (style.position === 'sticky' || style.position === 'fixed') {
                totalHeight += containerRef.current.getBoundingClientRect().height;
            }
        }

        // 设置CSS变量到根元素
        document.documentElement.style.setProperty('--sticky-top-offset', `${totalHeight}px`);
    }, []);

    const MAX_VISIBLE_TAGS = 5;

    // 在最前面插入 Blog Center
    const blogCenterItem = { id: 'blog-center', tag: t('blogCenter'), tagUrl: '', isBlogCenter: true };
    const allTagsWithCenter = [blogCenterItem, ...categoryTagList];

    const visibleTags = isPad ? [] : allTagsWithCenter.slice(0, MAX_VISIBLE_TAGS);
    const hiddenTags = isPad ? allTagsWithCenter : allTagsWithCenter.slice(MAX_VISIBLE_TAGS);
    const hasMoreTags = hiddenTags.length > 0;

    const getCategoryUrl = (item: any) => {
        // Blog Center 跳转到博客首页
        if (item.id === 'blog-center') {
            return `/${locale}/blog`;
        }
        const slug = item.tagUrl;
        return `/${locale}/blog/category/${slug}`;
    };

    const handleAllClick = () => {
        if (isPad) {
            setShowModal(true);
        } else {
            setShowDropdown(!showDropdown);
        }
    };

    const handleModalItemClick = () => {
        setShowModal(false);
    };

    // 检查文本是否被截断，如果是则显示title
    const handleMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
        const target = e.currentTarget;
        if (target.scrollWidth > target.clientWidth) {
            target.setAttribute('title', target.textContent || '');
        }
    };

    return (
        <>
            <div className="blog-category-nav-content" ref={containerRef}>
                <h1 className="blog-category-nav-content-title">
                    {categoryData.categoryName}
                </h1>
                {/* categoryTagList */}
                <div className="blog-category-nav-content-item">
                    {visibleTags.map((item: any, index: number) => {
                        return (
                            <a
                                key={index}
                                href={getCategoryUrl(item)}
                                className={`blog-category-nav-content-item-title-text ${item.isBlogCenter ? 'blog-center' : ''}`}
                                onMouseEnter={handleMouseEnter}
                            >
                                {item.isBlogCenter && (
                                    <svg className="blog-center-icon" width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M0.799805 15.675V5.58113C0.799805 2.94069 2.94044 0.800049 5.58122 0.800049H13.0187C15.6592 0.800049 17.7998 2.94069 17.7998 5.58113V13.019C17.7998 15.6594 15.6592 17.8 13.0187 17.8H2.9248C1.75112 17.8 0.799805 16.8487 0.799805 15.675Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M5.0498 9.30005H8.76872C9.94206 9.30005 10.8934 10.2514 10.8934 11.425C10.8934 12.5987 9.94206 13.55 8.76872 13.55H5.0498V6.11255C5.0498 5.52571 5.52546 5.05005 6.1123 5.05005H7.70588C8.87956 5.05005 9.83122 6.00137 9.83122 7.17505C9.83122 8.34873 8.87956 9.30005 7.70588 9.30005H6.1123" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M13.5498 13.55H13.5606" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                                {item.tag}
                            </a>
                        );
                    })}

                    {hasMoreTags && (
                        <div
                            ref={dropdownRef}
                            className={`blog-category-nav-content-item-dropdown ${showDropdown ? 'active' : ''}`}
                            onMouseEnter={() => !isPad && setShowDropdown(true)}
                            onMouseLeave={() => !isPad && setShowDropdown(false)}
                            onClick={handleAllClick}
                        >
                            <div className="blog-category-nav-content-item-title-text">
                                {t('all')}
                                <svg
                                    className={`dropdown-arrow ${showDropdown ? 'open' : ''}`}
                                    width="18"
                                    height="18"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                >
                                    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>

                            <div className="blog-category-nav-content-item-dropdown-menu">
                                {hiddenTags.map((item: any, index: number) => {
                                    return (
                                        <a
                                            key={index}
                                            href={getCategoryUrl(item)}
                                            className={`blog-category-nav-content-item-dropdown-menu-item ${item.isBlogCenter ? 'blog-center' : ''}`}
                                            onMouseEnter={handleMouseEnter}
                                        >
                                            {item.isBlogCenter && (
                                                <img
                                                    src="https://websiteoss.ecoflow.com/upload/image/1761041493108/Group%2012.svg"
                                                    alt="Blog Center"
                                                    className="blog-center-icon"
                                                />
                                            )}
                                            {item.tag}
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
                {/* Modal Overlay and Content */}
                {isPad && showModal && (
                    <>
                        <div className="blog-category-modal-overlay" onClick={() => setShowModal(false)}></div>
                        <div className="blog-category-modal">
                            <div className="blog-category-modal-header">
                                <h3>{categoryData.categoryName}</h3>
                                <button
                                    className="blog-category-modal-close"
                                    onClick={() => setShowModal(false)}
                                    aria-label="Close"
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="blog-category-modal-content">
                                {hiddenTags.map((item: any, index: number) => {
                                    return (
                                        <a
                                            key={index}
                                            href={getCategoryUrl(item)}
                                            className={`blog-category-modal-item ${item.isBlogCenter ? 'blog-center' : ''}`}
                                            onClick={handleModalItemClick}
                                        >
                                            {item.isBlogCenter && (
                                                <img
                                                    src="https://websiteoss.ecoflow.com/upload/image/1761041493108/Group%2012.svg"
                                                    alt="Blog Center"
                                                    className="blog-center-icon"
                                                />
                                            )}
                                            <span>{item.tag}</span>
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>

        </>
    );
}
